/**
 * @module app.core
 */

import { autoinject } from 'aurelia-framework';
import { AssessmentContext } from 'app/core/lib/assessment-context';
import { apiCall, Api } from 'app/shared/lib/api/api';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { IQuestionComponent, IQuestionSavedResponse, IQuestionConfig } from 'app/core/components/assessments/interfaces';
import { AppLogger } from 'app/shared/lib/logging/app-logger';
import { QuestionBase } from 'app/core/components/assessments/questions/question-base';
import { TaskService } from 'app/core/services/task-service';
import { ITasksConfig } from 'app/core/models/essette-interfaces';
import { getReference } from 'app/shared/models/essette-types';
import * as Core from 'app/core/models/essette-api';


@autoinject
export class QuestionSuggestTaskCustomElement extends QuestionBase implements IQuestionComponent {

    ////****************** Fields and Properties **************************************////

    private targetId: string | null = null;
    private targetType: string | null = null;
    private taskConfig: ITasksConfig | null = null;

    private taskDefinition: Core.TaskDefinition | null = null;
    protected tasks: ReadonlyArray<Core.Task> = [];
    protected addTask: boolean = false;


    ////****************** Lifecycle events (in order of execution) ******************////
    constructor(
        protected readonly context: AssessmentContext,
        private readonly api: Api,
        private readonly service: TaskService,
        private readonly logger: AppLogger) {

        super(context);
        this.logger.source = this;

        if(!context || !context.currentSession) {
            throw new Error("No Assessment context is available");
        }

        if(!this.context.questionComponents.includes(this)) {
            this.context.questionComponents.push(this);
        }
    }

    protected async activate(config: IQuestionConfig): Promise<void> {
        this.question = config.question;
        this.required = !!this.question.flag?.isRequired;
        this.showHistory = config.showHistory;
        this.readonly = config.readonly;
        this.isValid = !this.required;
        this.taskConfig = this.context.taskConfig;


        if(!(this.taskConfig && this.taskConfig.serviceRootUrl && this.taskConfig.targetType && this.taskConfig.addTargetProperty)) {
            throw new Error('Task configuration object is not correctly configured');
        }
        this.service.configure(this.taskConfig);

        const attachedTo = this.context?.attachment?.attachedTo || null;
        if(!(attachedTo && attachedTo.id && attachedTo.type && attachedTo.type.code)) {
            throw new Error('Unable to read property [attachedTo] on assessment attachment');
        }
        this.targetId = attachedTo.id;
        this.targetType = this.taskConfig.taskTargetType ?? this.taskConfig.targetType;
        this.loadData();
    }


    ////****************** Events ***********************************************////


    ////****************** Functions ***********************************************////
    private async loadData(): Promise<void> {
        if(!this.question) {
            throw new Error('Configuration missing required property: [question]');
        }

        const taskDefinitionId = String(this.question.relatedRecordID || null);
        this.loadTaskDefinition(taskDefinitionId);
        this.findMatchingTasksOnTarget(taskDefinitionId);
    }


    ////****************** Service Calls ********************************************////
    @apiCall
    private async loadTaskDefinition(taskDefinitionId: string): Promise<void> {
        try {
            this.state.beginLoad();
            const response = await this.service.loadTaskDefinition(taskDefinitionId, [], "tasks");
            this.taskDefinition = response.results[0];
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async findMatchingTasksOnTarget(taskDefinitionId: string): Promise<void> {
        if(!this.targetId) { throw new Error("Missing properties: [targetId]"); }
        if(!this.targetType) { throw new Error("Missing properties: [targetType]"); }
        if(!(this.taskConfig && (this.taskConfig.taskServiceRootUrl || this.taskConfig.serviceRootUrl))) { throw new Error("Missing properties: [taskConfig]"); }

        const options: Partial<IQueryLoadOptions> = { all: true };
        const includes: string[] = ['metadata.completed.byUser', 'metadata.created.byUser'];

        try {
            this.state.beginLoad();
            const query = this.api
                .query(`${this.taskConfig.taskServiceRootUrl ?? this.taskConfig.serviceRootUrl}/${this.targetId}/tasks`)
                .applyStandardLoadOptions(options)
                .include(...includes)
                .filterIdentifier('definition.id', taskDefinitionId);

            const response = await this.api.get(query);
            this.tasks = response.results;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async addTaskToTarget(task: Core.Task): Promise<boolean> {
        try {
            this.state.beginSave();
            const response = await this.service.insertTask(task, false, "tasks");
            if(response.isSuccessful) {
                this.addTask = false;
                this.loadData();
            }

            return response.isSuccessful;
        } catch(error) {
            throw error;
        } finally {
            this.state.endSave();
        }
    }


    ////****************** IQuestionComponent ****************************************************////
    public async saveAnswer(session?: Core.AssessmentSession): Promise<IQuestionSavedResponse> {
        const saveResponse: IQuestionSavedResponse = {
            question: this.question,
            isSuccessful: true
        };

        if(this.addTask) {
            if(!(this.taskConfig && (this.taskConfig.addTaskTargetProperty || this.taskConfig.addTargetProperty))) {
                throw new Error('Task Configuration missing callback: [addTargetProperty]');
            }

            const newTask: Core.Task = {
                definition: getReference(this.taskDefinition),
                delegated: {
                    queue: this.taskDefinition?.defaultQueue ? {
                        id: this.taskDefinition.defaultQueue.id
                    } : null
                }
            };
            if(this.taskConfig.addTaskTargetProperty) {
                this.taskConfig.addTaskTargetProperty(newTask);
            }
            else if(this.taskConfig.addTargetProperty) {
                this.taskConfig.addTargetProperty(newTask);
            }

            saveResponse.isSuccessful = await this.addTaskToTarget(newTask);
        }

        return saveResponse;
    }

    public getAnswers(): Core.AssessmentAnswer[] {
        return [];
    }

}

